/**
 * The people, the machines and the creatures, as SVG.
 *
 * Everything here is a pure function of its props. No component reads a clock, generates a random
 * number at render time, or holds state — so the same developer looks the same on every render,
 * across reloads, and in a screenshot taken a week apart. Variation that should differ between
 * people is derived from their id; variation that should differ over time is driven by CSS
 * animation, which costs no React work at all.
 *
 * **The drawing style.** Cartoon figures with a big head, a dark outline and flat fills with one
 * shade. The outline is what makes a figure read at a glance against a busy room, and it is drawn
 * the cheap way: a limb is the same path twice, a wide dark stroke under a narrower coloured one.
 *
 * **Units.** A standing figure has its feet at the origin and is about 130 units tall. Callers
 * place and scale with a transform rather than passing sizes down.
 */

import { memo } from 'react'
import {
  COMPANIONS, EVERYDAY_OUTFITS, EXTRAS, HAIR_STYLES, HATS, HEADPHONES, HELD_ITEMS, LAPTOPS, OUTFITS, SKIN_TONES,
  type CompanionKind, type ExtraKind, type FacialHair, type HairStyle, type HatKind, type HeadphoneModel, type HeldKind, type LaptopModel, type Outfit,
} from './wardrobe'
import { DeskLaptop, HeldLaptop, Headphones } from './Gear'
import { ChosenHat, Companion, Extra, HeldItem, ProOutfit } from './ProItems'
import { useScenery, type Headwear as HeadwearKind } from './scenery'
import type { Avatar, Priority } from '../lib/types'
import { seedFrom } from './layout'
import { PRIORITY_ACCENT, PRIORITY_MARKS } from './visual'

/** The outline colour. One ink for every figure, so they read as one cast. */
const INK = '#1a1f2c'

/* ------------------------------------------------------------------ identity */

/*
  The avatar is stored as palette keys, not colours.

  `DeveloperAvatar` in the domain says so outright — "Stored as keys, not hex, so a theme change is
  one edit" — and skin tone and hair style are keys while hair and shirt colour are hex. Passing a
  key straight to an SVG `fill` produces an invalid colour, which browsers render as black. These
  maps are the translation, and they cover every value the domain defines.
*/

export type { FacialHair, HairStyle, Outfit } from './wardrobe'

export type Gender = 'male' | 'female' | 'unspecified'

/**
 * Styles the id-derived fallback may pick from.
 *
 * Short ones only. The fallback used to draw from every style, so a developer who had never opened
 * the avatar editor could come out with a ponytail — which is how a man ended up drawn as a girl.
 * Long hair, a bun or a ponytail is something a person chooses, not something a hash hands out.
 */
const FALLBACK_HAIR: HairStyle[] = ['short', 'buzz', 'curly', 'afro', 'spiky', 'wavy']

const SKIN_KEYS = Object.keys(SKIN_TONES)
const HAIR_COLORS = ['#2E2A27', '#4A342A', '#7B4B28', '#C9A227', '#9A9A9A', '#1B1B1B']
const SHIRT_COLORS = ['#3B5BDB', '#2F9E68', '#B5462C', '#6A4FB8', '#1F7A8C', '#C2560B']

export interface Look {
  skin: string
  hair: string
  hairStyle: HairStyle
  shirt: string
  outfit: Outfit
  glasses: boolean
  headphones: HeadphoneModel
  laptop: LaptopModel
  facialHair: FacialHair
  /** Chosen by the person. Drives the face: lashes, brows, lips. */
  gender: Gender
  /** The extra items (docs/refactor/47): a hat, what is held when hunting, a companion. */
  hat: HatKind
  held: HeldKind
  companion: CompanionKind
  extra: ExtraKind
  /** Seconds, so a room full of people does not blink in unison. */
  blinkDelay: number
}

/** Accepts a hex as-is and a palette key by lookup, so neither shape can reach a paint attribute. */
function colorFrom(value: string | undefined, palette: Record<string, string>, fallback: string): string {
  if (!value) return fallback
  if (value.startsWith('#')) return value
  return palette[value] ?? fallback
}

/**
 * What a developer looks like.
 *
 * Prefers the avatar the organization configured, because a person choosing their own appearance
 * beats an algorithm guessing it — §40 of the brief is explicit that nothing about how somebody
 * looks is inferred. The id-derived fallback exists only so that a developer who has never opened
 * the avatar editor still has a consistent, recognisable figure instead of a default everyone
 * shares.
 */
export function lookFor(developerId: string, avatar?: Avatar | null): Look {
  const seed = seedFrom(developerId)

  const pick = <T,>(list: readonly T[], offset: number): T => list[(seed >> offset) % list.length]!

  const style = avatar?.hair as HairStyle | undefined

  return {
    skin: colorFrom(avatar?.skinTone, SKIN_TONES, SKIN_TONES[pick(SKIN_KEYS, 0)]!),
    hair: avatar?.hairColor?.startsWith('#') ? avatar.hairColor : pick(HAIR_COLORS, 3),
    hairStyle: style && HAIR_STYLES.includes(style) ? style : pick(FALLBACK_HAIR, 6),
    shirt: avatar?.shirtColor?.startsWith('#') ? avatar.shirtColor : pick(SHIRT_COLORS, 9),
    outfit: avatar?.outfit && (OUTFITS as string[]).includes(avatar.outfit) ? (avatar.outfit as Outfit) : pick(EVERYDAY_OUTFITS, 10),
    glasses: avatar?.glasses ?? (seed >> 12) % 3 === 0,
    headphones: headphonesOf(avatar, seed),
    laptop: avatar?.laptop && (LAPTOPS as string[]).includes(avatar.laptop) ? (avatar.laptop as LaptopModel) : 'tablet',
    facialHair: (avatar?.facialHair as FacialHair) ?? 'none',
    gender: avatar?.gender === 'male' || avatar?.gender === 'female' ? avatar.gender : 'unspecified',
    hat: avatar?.hat && (HATS as string[]).includes(avatar.hat) ? (avatar.hat as HatKind) : 'none',
    held: avatar?.held && (HELD_ITEMS as string[]).includes(avatar.held) ? (avatar.held as HeldKind) : 'racket',
    companion: avatar?.companion && (COMPANIONS as string[]).includes(avatar.companion) ? (avatar.companion as CompanionKind) : 'none',
    extra: avatar?.extra && (EXTRAS as string[]).includes(avatar.extra) ? (avatar.extra as ExtraKind) : 'none',
    blinkDelay: ((seed >> 4) % 40) / 10,
  }
}

/**
 * Which headphones: the model when one was chosen; otherwise the on/off switch from before there
 * were models; otherwise, for somebody who never opened the builder, a headset now and then.
 */
function headphonesOf(avatar: Avatar | null | undefined, seed: number): HeadphoneModel {
  if (avatar?.headphones && (HEADPHONES as string[]).includes(avatar.headphones)) return avatar.headphones as HeadphoneModel
  if (avatar?.headset != null) return avatar.headset ? 'headset' : 'none'
  return (seed >> 14) % 3 === 0 ? 'headset' : 'none'
}

/* ------------------------------------------------------------------ helpers */

/** A limb: the same path twice, outline under colour. */
function Limb({ d, color, width }: { d: string; color: string; width: number }) {
  return (
    <>
      <path d={d} fill="none" stroke={INK} strokeWidth={width + 3.2} strokeLinecap="round" strokeLinejoin="round" />
      <path d={d} fill="none" stroke={color} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" />
    </>
  )
}

/**
 * An arm from the shoulder. In a hoodie, sleeve all the way; in a T-shirt, a short sleeve and the
 * arm below it — the path starts at the shoulder, so the sleeve is its first few units.
 */
function Arm({ d, look }: { d: string; look: Look }) {
  const { outfit } = useScenery()
  if (outfit !== 'tshirt' && look.outfit !== 'tee') return <Limb d={d} color={look.shirt} width={7.5} />

  return (
    <>
      <path d={d} fill="none" stroke={INK} strokeWidth={10.7} strokeLinecap="round" strokeLinejoin="round" />
      <path d={d} fill="none" stroke={look.skin} strokeWidth={7.5} strokeLinecap="round" strokeLinejoin="round" />
      <path d={d} fill="none" stroke={look.shirt} strokeWidth={9} strokeLinecap="butt" strokeDasharray="8 400" />
    </>
  )
}

/**
 * Circles that merge into one outlined shape — curls, an afro, a cloud of hair.
 *
 * Drawn as every outline first and every fill second, so the circles' own edges disappear inside
 * the shape and only its silhouette keeps a line.
 */
function Cloud({ circles, color }: { circles: Array<[number, number, number]>; color: string }) {
  return (
    <>
      {circles.map(([cx, cy, r], i) => (
        <circle key={`o${i}`} cx={cx} cy={cy} r={r} fill={INK} stroke={INK} strokeWidth="3.2" />
      ))}
      {circles.map(([cx, cy, r], i) => (
        <circle key={`f${i}`} cx={cx} cy={cy} r={r} fill={color} />
      ))}
    </>
  )
}

/* --------------------------------------------------------------------- head */

export type Expression = 'focused' | 'calm' | 'rest'

/**
 * The head, centred at (0, -100) with a radius of about 23.
 *
 * Exported on its own because the portrait in the panels is the same face. A second, simpler
 * drawing of each person for the sidebar would drift from the one on the floor within a release.
 */
export const Head = memo(function Head({ look, expression }: { look: Look; expression: Expression }) {
  const female = look.gender === 'female'
  const { headwear } = useScenery()

  return (
    <g>
      <BackHair style={look.hairStyle} color={look.hair} />

      {/* Ears first, so the face overlaps where they join. */}
      <circle cx="-22.5" cy="-98" r="5" fill={look.skin} stroke={INK} strokeWidth="1.5" />
      <circle cx="22.5" cy="-98" r="5" fill={look.skin} stroke={INK} strokeWidth="1.5" />

      <ellipse cx="0" cy="-100" rx="23" ry="22" fill={look.skin} stroke={INK} strokeWidth="1.8" />

      <FacialHairMark style={look.facialHair} color={look.hair} />

      <FrontHair style={look.hairStyle} color={look.hair} />

      <Brows expression={expression} color={look.hairStyle === 'bald' ? INK : look.hair} female={female} />

      {/* The eyes blink every few seconds, each person on their own clock. */}
      <g className="bh-eyes" style={{ animationDelay: `${look.blinkDelay}s` }}>
        <Eye x={-8.5} female={female} side="left" />
        <Eye x={8.5} female={female} side="right" />
      </g>

      <path d="M -1.2 -91.5 Q 0 -90 1.2 -91.5" fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="1.2" strokeLinecap="round" />

      <ellipse cx="-14.5" cy="-89" rx="3.6" ry="2.1" fill="#FF6F7D" opacity={female ? 0.42 : 0.3} />
      <ellipse cx="14.5" cy="-89" rx="3.6" ry="2.1" fill="#FF6F7D" opacity={female ? 0.42 : 0.3} />

      <Mouth expression={expression} female={female} />

      {/* The kids' accessories (docs/refactor/48): bubblegum at the mouth, stars on the cheeks. */}
      <Extra kind={look.extra} />

      {/* Hair that falls in front of the face, drawn over the cheeks: the locks that frame it. */}
      <FaceLocks style={look.hairStyle} color={look.hair} />

      {look.glasses && (
        <g>
          <circle cx="-8.5" cy="-98" r="6.4" fill="rgba(255,255,255,0.12)" stroke={INK} strokeWidth="1.5" />
          <circle cx="8.5" cy="-98" r="6.4" fill="rgba(255,255,255,0.12)" stroke={INK} strokeWidth="1.5" />
          <path d="M -2.1 -98.6 Q 0 -100.2 2.1 -98.6" fill="none" stroke={INK} strokeWidth="1.4" />
          <path d="M -14.9 -99 L -21.5 -101 M 14.9 -99 L 21.5 -101" stroke={INK} strokeWidth="1.3" />
        </g>
      )}

      <Headphones model={look.headphones} />

      {/* The hat the person chose wins over the season's (docs/refactor/47). */}
      {look.hat !== 'none' ? <ChosenHat kind={look.hat} /> : <Headwear kind={headwear} />}
    </g>
  )
})

/**
 * What goes on heads for an occasion. Drawn last, over hair and headset, and sized to the head
 * (centred at 0, -100, top near -122) rather than to any hair style.
 */
function Headwear({ kind }: { kind: HeadwearKind }) {
  switch (kind) {
    case 'santa':
      return (
        <g>
          <path d="M -21 -119 Q -14 -150 10 -153 Q 27 -154 31 -136 Q 21 -141 13 -140 Q 17 -130 21 -119 Z" fill="#D7263D" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
          <rect x="-25" y="-124" width="50" height="9" rx="4.5" fill="#F4F4F4" stroke={INK} strokeWidth="1.5" />
          <circle cx="31" cy="-136" r="5" fill="#F4F4F4" stroke={INK} strokeWidth="1.4" />
        </g>
      )
    case 'bunny':
      return (
        <g>
          <g transform="rotate(-14 -9 -140)">
            <ellipse cx="-9" cy="-140" rx="6.5" ry="18" fill="#F7F7F7" stroke={INK} strokeWidth="1.5" />
            <ellipse cx="-9" cy="-139" rx="3" ry="12" fill="#F7A8C0" />
          </g>
          <g transform="rotate(18 10 -138)">
            <ellipse cx="10" cy="-138" rx="6.5" ry="17" fill="#F7F7F7" stroke={INK} strokeWidth="1.5" />
            <ellipse cx="10" cy="-137" rx="3" ry="11" fill="#F7A8C0" />
          </g>
          <path d="M -22 -113 Q 0 -134 22 -113" fill="none" stroke={INK} strokeWidth="4.2" strokeLinecap="round" />
          <path d="M -22 -113 Q 0 -134 22 -113" fill="none" stroke="#F7A8C0" strokeWidth="2.4" strokeLinecap="round" />
        </g>
      )
    case 'party':
      return (
        <g transform="rotate(-12 0 -122)">
          <path d="M -12 -120 L 12 -120 L 1 -157 Z" fill="#8E7CC3" stroke={INK} strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M -8 -131 L 8 -131 M -5 -141 L 5 -141" stroke="#F5C542" strokeWidth="2.4" />
          <circle cx="1" cy="-158" r="4.2" fill="#F5C542" stroke={INK} strokeWidth="1.2" />
          <path d="M -13 -120 L 13 -120" stroke={INK} strokeWidth="2" />
        </g>
      )
    case 'witch':
      return (
        <g>
          <ellipse cx="0" cy="-120" rx="33" ry="6" fill="#2B1D3A" stroke={INK} strokeWidth="1.5" />
          <path d="M -15 -122 L 5 -162 Q 9 -160 13 -150 L 16 -122 Z" fill="#2B1D3A" stroke={INK} strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M -14.6 -127 L 15.6 -127 L 15.9 -122.5 L -15 -122.5 Z" fill="#8E44AD" />
          <rect x="-3" y="-128.5" width="6" height="6" fill="none" stroke="#F5C542" strokeWidth="1.3" />
        </g>
      )
    case 'mask':
      return (
        <g>
          <path
            fillRule="evenodd"
            d="M -22 -104 Q -10 -112 0 -102 Q 10 -112 22 -104 Q 20 -90 8 -92 Q 2 -93 0 -96 Q -2 -93 -8 -92 Q -20 -90 -22 -104 Z M -13.5 -98 a 5 3.8 0 1 0 10 0 a 5 3.8 0 1 0 -10 0 Z M 3.5 -98 a 5 3.8 0 1 0 10 0 a 5 3.8 0 1 0 -10 0 Z"
            fill="#7B2CBF"
            stroke="#F5C542"
            strokeWidth="1.3"
          />
          <path d="M 22 -104 Q 30 -118 26 -128 Q 34 -118 24 -103" fill="#46A758" stroke={INK} strokeWidth="1" />
        </g>
      )
    default:
      return null
  }
}

function Eye({ x, female, side }: { x: number; female: boolean; side: 'left' | 'right' }) {
  // Both irises sit a little right of centre: the figures face the bug they are chasing.
  const out = side === 'left' ? -1 : 1

  return (
    <g>
      <ellipse cx={x} cy="-98" rx={female ? 4.9 : 4.6} ry={female ? 6 : 5.6} fill="#FFFFFF" stroke={INK} strokeWidth="1.1" />
      <circle cx={x + 0.8} cy="-97.4" r={female ? 3.7 : 3.5} fill="#5A3A26" />
      <circle cx={x + 0.9} cy="-97.2" r={female ? 2.3 : 2.2} fill="#1E1512" />
      <circle cx={x + 2.1} cy="-99.6" r="1.4" fill="#FFFFFF" />
      <circle cx={x - 0.3} cy="-95.8" r="0.7" fill="#FFFFFF" />
      {female && (
        // Lashes on the outer corner: two short strokes are all it takes at this size.
        <path
          d={`M ${x + out * 3.4} -101.8 l ${out * 2.4} -1.9 M ${x + out * 1.6} -103.1 l ${out * 1.4} -2.2`}
          stroke={INK}
          strokeWidth="1.2"
          strokeLinecap="round"
        />
      )}
    </g>
  )
}

function Brows({ expression, color, female }: { expression: Expression; color: string; female: boolean }) {
  // Inner ends down reads as determination; level reads as ease. Nothing in between is needed.
  // Drawn thinner and higher for a woman: the single biggest cue on a face this small.
  const d = female
    ? expression === 'focused'
      ? 'M -12.5 -108.5 Q -8.5 -111.5 -4.6 -107.4 M 4.6 -107.4 Q 8.5 -111.5 12.5 -108.5'
      : 'M -12.6 -108 Q -8.5 -111.6 -4.6 -109 M 4.6 -109 Q 8.5 -111.6 12.6 -108'
    : expression === 'focused'
      ? 'M -13 -107.5 Q -8.5 -110 -4.2 -105.8 M 4.2 -105.8 Q 8.5 -110 13 -107.5'
      : 'M -12.5 -107 Q -8.5 -109.5 -4.5 -107.5 M 4.5 -107.5 Q 8.5 -109.5 12.5 -107'

  return <path d={d} fill="none" stroke={color} strokeWidth={female ? 1.6 : 2.3} strokeLinecap="round" />
}

function Mouth({ expression, female }: { expression: Expression; female: boolean }) {
  if (expression === 'focused') {
    return (
      <g>
        <path d="M -6.5 -86.5 Q 0 -79.2 6.5 -86.5 Q 0 -85 -6.5 -86.5 Z" fill={female ? '#8A2A3E' : '#6B2430'} stroke={INK} strokeWidth="1.2" strokeLinejoin="round" />
        <path d="M -4.6 -85.7 Q 0 -84.6 4.6 -85.7 L 4 -84.3 Q 0 -83.5 -4 -84.3 Z" fill="#FFFFFF" />
        <ellipse cx="0" cy="-82" rx="2.4" ry="1.1" fill="#E0707A" />
      </g>
    )
  }

  if (female) {
    // A smile with colour in the lips rather than a line.
    return (
      <path
        d={expression === 'calm'
          ? 'M -4.8 -85.8 Q 0 -81.6 4.8 -85.8 Q 0 -84.3 -4.8 -85.8 Z'
          : 'M -4.2 -85.4 Q 0 -82.4 4.2 -85.4 Q 0 -84.3 -4.2 -85.4 Z'}
        fill="#D9667A"
        stroke={INK}
        strokeWidth="0.9"
        strokeLinejoin="round"
      />
    )
  }

  const d = expression === 'calm' ? 'M -5 -86 Q 0 -82 5 -86' : 'M -4 -85.5 Q 0 -83 4 -85.5'
  return <path d={d} fill="none" stroke={INK} strokeWidth="1.5" strokeLinecap="round" />
}

/** Long hair's front locks, over the cheeks, so the hair frames the face instead of sitting behind it. */
function FaceLocks({ style, color }: { style: HairStyle; color: string }) {
  if (style === 'bob') {
    return (
      <path
        d="M -23.6 -99 C -25 -91 -24 -85 -21 -81 C -19.8 -86 -19.6 -92 -19.8 -97 Z M 23.6 -99 C 25 -91 24 -85 21 -81 C 19.8 -86 19.6 -92 19.8 -97 Z"
        fill={color}
        stroke={INK}
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    )
  }
  if (style !== 'long') return null

  return (
    <path
      d="M -23.4 -99 C -25.5 -89 -24.5 -80 -20.5 -73 C -18.5 -79 -18.4 -89 -19.2 -97 Z M 23.4 -99 C 25.5 -89 24.5 -80 20.5 -73 C 18.5 -79 18.4 -89 19.2 -97 Z"
      fill={color}
      stroke={INK}
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  )
}

/** Hair that sits behind the head: length, a ponytail, a bun, the body of an afro. */
function BackHair({ style, color }: { style: HairStyle; color: string }) {
  switch (style) {
    case 'long':
      // A full shape behind the head, falling past the shoulders with a slight outward flip.
      return (
        <g>
          <path
            d="M 0 -129 C -20 -129 -31 -116 -30 -98 C -29.5 -86 -32.5 -71 -29 -59 C -27 -52.5 -20 -50.5 -15.5 -55 C -18 -64 -18.5 -74 -19 -84 L 19 -84 C 18.5 -74 18 -64 15.5 -55 C 20 -50.5 27 -52.5 29 -59 C 32.5 -71 29.5 -86 30 -98 C 31 -116 20 -129 0 -129 Z"
            fill={color}
            stroke={INK}
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          <path d="M -26 -92 C -27 -80 -27 -70 -24 -62 M 26 -92 C 27 -80 27 -70 24 -62" fill="none" stroke="#000000" strokeOpacity="0.18" strokeWidth="1.4" strokeLinecap="round" />
        </g>
      )

    case 'ponytail':
      // High on the crown and swinging out to one side.
      return (
        <g>
          <path d="M 8 -125 C 25 -135 39 -122 36 -101 C 34 -89 28 -79 21 -75 C 26 -88 26 -104 11 -113 Z" fill={color} stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M 28 -118 C 33 -108 32 -96 27 -86" fill="none" stroke="#000000" strokeOpacity="0.18" strokeWidth="1.3" strokeLinecap="round" />
          <circle cx="15" cy="-121" r="3" fill="#E5484D" stroke={INK} strokeWidth="1" />
        </g>
      )

    case 'bun':
      return (
        <g>
          <circle cx="0" cy="-131" r="10" fill={color} stroke={INK} strokeWidth="1.6" />
          <path d="M -6 -134 Q 0 -138 6 -133" fill="none" stroke="#000000" strokeOpacity="0.2" strokeWidth="1.3" strokeLinecap="round" />
          <rect x="-6.5" y="-124.5" width="13" height="3.4" rx="1.7" fill="#E5484D" stroke={INK} strokeWidth="0.9" />
        </g>
      )

    case 'afro':
      return <Cloud color={color} circles={[[0, -116, 27], [-21, -103, 13], [21, -103, 13], [-14, -126, 14], [14, -126, 14]]} />

    case 'bob':
      // Chin length and rounded, curling in under the jaw.
      return (
        <path
          d="M 0 -130 C -21 -130 -31 -116 -30.5 -99 C -30 -90 -30.5 -83 -26.5 -78.5 C -22.5 -75.5 -18.5 -77.5 -17.5 -82 L 17.5 -82 C 18.5 -77.5 22.5 -75.5 26.5 -78.5 C 30.5 -83 30 -90 30.5 -99 C 31 -116 21 -130 0 -130 Z"
          fill={color}
          stroke={INK}
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      )

    case 'wavy':
      // A little fullness at the nape, so the waves read as volume and not a cap.
      return (
        <path
          d="M -24 -104 C -28.5 -95 -27 -87 -22 -84 C -21 -88 -21.5 -93 -21 -96 Z M 24 -104 C 28.5 -95 27 -87 22 -84 C 21 -88 21.5 -93 21 -96 Z"
          fill={color}
          stroke={INK}
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
      )

    default:
      return null
  }
}

/** Hair over the top of the head. Never over the eyes. */
function FrontHair({ style, color }: { style: HairStyle; color: string }) {
  const shine = <path d="M -11 -121.5 Q -3 -125.5 6 -123.5" fill="none" stroke="#FFFFFF" strokeOpacity="0.28" strokeWidth="2" strokeLinecap="round" />

  switch (style) {
    case 'bald':
      return <path d="M -9 -117 Q -3 -120.5 4 -119.5" fill="none" stroke="#FFFFFF" strokeOpacity="0.35" strokeWidth="2" strokeLinecap="round" />

    case 'buzz':
      return (
        <path
          d="M -23 -99 C -24 -117 -13 -125 0 -125 C 13 -125 24 -117 23 -99 C 17 -108 8 -111 0 -111 C -8 -111 -17 -108 -23 -99 Z"
          fill={color}
          stroke={INK}
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      )

    case 'curly':
      return (
        <Cloud
          color={color}
          circles={[[0, -114, 16], [-19, -107, 7], [-13, -117, 8], [-3, -122, 8.5], [8, -121, 8], [16, -114, 7.5], [21, -104, 5.5]]}
        />
      )

    case 'afro':
      return <path d="M -21 -102 C -20 -114 -10 -118 0 -118 C 10 -118 20 -114 21 -102 C 14 -108 -14 -108 -21 -102 Z" fill={color} />

    case 'spiky':
      // Tufts pushed up and forward — the hair of somebody mid-sprint.
      return (
        <g>
          <path
            d="M -23.5 -97 C -26 -112 -19 -122 -11 -124.5 L -14 -133 L -4 -127.5 L -1 -137 L 5 -127.5 L 14 -133.5 L 12.5 -124 C 21 -121 26.5 -112 23.5 -97 C 21 -104 16 -109.5 9 -110 L 5 -104 L 1.5 -109.5 L -4 -103.5 L -7.5 -109.5 C -14 -108 -20 -103.5 -23.5 -97 Z"
            fill={color}
            stroke={INK}
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          {shine}
        </g>
      )

    case 'wavy':
      return (
        <g>
          <path
            d="M -24.5 -96 C -28.5 -114 -17 -129.5 0 -129.5 C 18 -129.5 28.5 -117 24.5 -97 C 23 -103 20 -107 16 -109 C 17 -104 15 -101 12 -100 C 12 -106 8 -110 2 -111 C 3 -106 0 -103 -4 -102 C -4 -107 -7 -110 -11 -111 C -13 -105 -18 -101 -24.5 -96 Z"
            fill={color}
            stroke={INK}
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          <path d="M -15 -118 Q -8 -124 0 -121 M 4 -121 Q 11 -125 17 -118" fill="none" stroke="#000000" strokeOpacity="0.2" strokeWidth="1.3" strokeLinecap="round" />
          {shine}
        </g>
      )

    case 'bob':
      // A straight fringe, the bob's own.
      return (
        <g>
          <path
            d="M -24.5 -95 C -27 -115 -15 -128.5 0 -128.5 C 15 -128.5 27 -115 24.5 -95 C 22.5 -102 20 -106 15.5 -106.5 L -15.5 -106.5 C -20 -106 -22.5 -102 -24.5 -95 Z"
            fill={color}
            stroke={INK}
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          <path d="M -9 -107 L -8 -113 M 0 -107 L 0 -114 M 9 -107 L 8 -113" stroke="#000000" strokeOpacity="0.18" strokeWidth="1.1" strokeLinecap="round" />
          {shine}
        </g>
      )

    case 'long':
    case 'ponytail':
      // A fringe swept across from a parting on the right, dipping low on the left.
      return (
        <g>
          <path
            d="M -24 -95 C -26.5 -114 -15 -127 2 -127.5 C 17 -127.5 26 -116 24 -98 C 22 -106.5 17 -112 8.5 -113 C 4.5 -107.5 -5 -101.5 -14 -100.5 C -18.5 -100 -21.5 -98.5 -24 -95 Z"
            fill={color}
            stroke={INK}
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          <path d="M 7 -112 C 2 -106 -6 -103 -13 -102" fill="none" stroke="#000000" strokeOpacity="0.18" strokeWidth="1.2" strokeLinecap="round" />
          {shine}
        </g>
      )

    case 'bun':
      return (
        <g>
          <path
            d="M -23.5 -97 C -26 -117 -13 -126 0 -126 C 13 -126 26 -117 23.5 -97 C 16 -109 6 -113 0 -113 C -6 -113 -16 -109 -23.5 -97 Z"
            fill={color}
            stroke={INK}
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          {shine}
        </g>
      )

    default:
      // Short, with a fringe swept to one side.
      return (
        <g>
          <path
            d="M -23.5 -97 C -26 -116 -14 -127 0 -127 C 15 -127 26 -116 23.5 -97 C 21 -104 17 -109 11 -110 C 8 -106 3 -104 -2 -105 C -1 -109 -3 -111 -5 -112 C -9 -107 -15 -104 -23.5 -97 Z"
            fill={color}
            stroke={INK}
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          {shine}
        </g>
      )
  }
}

/** Stubble through to a full beard, following the same vocabulary the settings screen offers. */
function FacialHairMark({ style, color }: { style: FacialHair; color: string }) {
  switch (style) {
    case 'stubble':
      return <path d="M -19 -93 C -16 -81 -8 -78 0 -78 C 8 -78 16 -81 19 -93 C 12 -85 -12 -85 -19 -93 Z" fill={color} opacity="0.28" />

    case 'moustache':
      return <path d="M -7 -88 Q -3 -91.5 0 -89.2 Q 3 -91.5 7 -88 Q 3 -86.3 0 -87.6 Q -3 -86.3 -7 -88 Z" fill={color} stroke={INK} strokeWidth="0.8" />

    case 'goatee':
      return (
        <g fill={color} stroke={INK} strokeWidth="0.8">
          <path d="M -6 -88.2 Q -3 -90.8 0 -89 Q 3 -90.8 6 -88.2 Q 3 -86.8 0 -87.8 Q -3 -86.8 -6 -88.2 Z" />
          <path d="M -4 -81.5 Q 0 -79.5 4 -81.5 L 3 -77.2 Q 0 -75.5 -3 -77.2 Z" />
        </g>
      )

    case 'beard':
      return (
        <path
          d="M -22.5 -99 C -22.5 -83 -12 -74.5 0 -74.5 C 12 -74.5 22.5 -83 22.5 -99 C 18 -90 12 -86.5 6 -87 Q 0 -84.5 -6 -87 C -12 -86.5 -18 -90 -22.5 -99 Z"
          fill={color}
          stroke={INK}
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
      )

    default:
      return null
  }
}

/* ---------------------------------------------------------------- developer */

const PANTS = '#2D3A57'

const TORSO =
  'M -18 -71 Q -18 -75.5 -13 -75.5 L 13 -75.5 Q 18 -75.5 18 -71 L 20.5 -41 Q 21 -36 16 -36 L -16 -36 Q -21 -36 -20.5 -41 Z'

/** A hoodie, because it is what most of the people this screen shows are wearing. */
function Torso({ look }: { look: Look }) {
  const { outfit } = useScenery()

  return (
    <g>
      <rect x="-5" y="-81" width="10" height="8" fill={look.skin} stroke={INK} strokeWidth="1.2" />
      <rect x="-5" y="-78" width="10" height="4" fill="rgba(0,0,0,0.18)" />

      <path d={TORSO} fill={look.shirt} stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
      {/* One shade down the far side: enough to turn a flat shape into a body. */}
      <path d="M 6 -75.5 L 13 -75.5 Q 18 -75.5 18 -71 L 20.5 -41 Q 21 -36 16 -36 L 9 -36 Q 12 -56 6 -75.5 Z" fill="rgba(0,0,0,0.16)" />
      {look.outfit === 'armor' || look.outfit === 'robe' || look.outfit === 'jersey' ? (
        <ProOutfit outfit={look.outfit} shirt={look.shirt} torso={TORSO} />
      ) : (
        <OutfitDetail outfit={look.outfit} summer={outfit === 'tshirt'} />
      )}
      <path d="M -20.3 -40 L 20.3 -40" stroke="rgba(0,0,0,0.25)" strokeWidth="1.4" />

      {outfit === 'scarf' && (
        <g>
          <path d="M -13 -77 Q 0 -70 13 -77 L 13 -72 Q 0 -65 -13 -72 Z" fill="#C0392B" stroke={INK} strokeWidth="1.3" strokeLinejoin="round" />
          <path d="M 6 -70 L 11 -52 L 5 -51 L 2 -69 Z" fill="#C0392B" stroke={INK} strokeWidth="1.2" strokeLinejoin="round" />
          <path d="M 5 -58 L 10 -59 M 6 -55 L 11 -56" stroke="#F4D35E" strokeWidth="1.2" />
          <path d="M -9 -74 L 9 -74" stroke="#F4D35E" strokeWidth="1.2" strokeDasharray="3 3" />
        </g>
      )}
    </g>
  )
}

/**
 * What makes each outfit that outfit, drawn over the same torso: strings and a pocket on a hoodie,
 * an open zip over a white tee on a jacket, a crew neck and a print on a T-shirt, a knitted band on
 * a sweater, a collar and buttons on a shirt. The same few strokes at every size.
 */
function OutfitDetail({ outfit, summer }: { outfit: Outfit; summer: boolean }) {
  switch (outfit) {
    case 'jacket':
      return (
        <g>
          <path d="M -6 -75.5 L 6 -75.5 L 5 -36.5 L -5 -36.5 Z" fill="#EEF1F6" />
          <path d="M -6 -75.5 L -5 -36.5 M 6 -75.5 L 5 -36.5" stroke={INK} strokeWidth="1.2" />
          <path d="M -6 -75.5 L -11 -70 L -7 -66 M 6 -75.5 L 11 -70 L 7 -66" fill="none" stroke="rgba(0,0,0,0.4)" strokeWidth="1.3" strokeLinejoin="round" />
          <path d="M -3 -63 h 1.6 l 1 -2.5 l 1.6 4.8 l 1.1 -2.3 h 1.6" fill="none" stroke="#3E9BE6" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M -16 -47 L -9 -47 M 16 -47 L 9 -47" stroke="rgba(0,0,0,0.3)" strokeWidth="1.3" strokeLinecap="round" />
        </g>
      )
    case 'tee':
      return (
        <g>
          <path d="M -7 -75.5 Q 0 -69.5 7 -75.5" fill="none" stroke="rgba(0,0,0,0.4)" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="0" cy="-58" r="5.2" fill="rgba(255,255,255,0.85)" />
          <path d="M -3.2 -58 h 1.4 l 0.9 -2.2 l 1.4 4.2 l 1 -2 h 1.6" fill="none" stroke={INK} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      )
    case 'sweater':
      return (
        <g>
          <path d="M -8 -75.5 Q 0 -70 8 -75.5" fill="none" stroke="rgba(0,0,0,0.4)" strokeWidth="2.4" strokeLinecap="round" />
          <path d="M -19 -62 L 19 -62 L 19.6 -54 L -19.6 -54 Z" fill="rgba(255,255,255,0.28)" />
          <path d="M -18 -58 l 3 -2.5 l 3 2.5 l 3 -2.5 l 3 2.5 l 3 -2.5 l 3 2.5 l 3 -2.5 l 3 2.5 l 3 -2.5 l 3 2.5 l 3 -2.5" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="1.1" />
          <path d="M -20 -43 L 20 -43" stroke="rgba(0,0,0,0.22)" strokeWidth="2.6" strokeDasharray="1.6 1.6" />
        </g>
      )
    case 'shirt':
      return (
        <g>
          <path d="M -8 -75.5 L -1 -68 L -3 -64.5 L -10 -71 Z M 8 -75.5 L 1 -68 L 3 -64.5 L 10 -71 Z" fill="#F4F6FA" stroke={INK} strokeWidth="1" strokeLinejoin="round" />
          <path d="M 0 -67 L 0 -37" stroke="rgba(0,0,0,0.3)" strokeWidth="1.1" />
          {[-61, -54, -47].map((y) => <circle key={y} cx="1.8" cy={y} r="0.9" fill="#F4F6FA" />)}
          <path d="M -15 -62 L -8 -62 L -8 -56 L -15 -56 Z" fill="none" stroke="rgba(0,0,0,0.3)" strokeWidth="1" />
        </g>
      )
    default:
      return summer ? (
        <path d="M -7 -75.5 Q 0 -70 7 -75.5" fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="1.8" strokeLinecap="round" />
      ) : (
        <g>
          <path d="M -11 -75.5 Q 0 -64 11 -75.5" fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="2.2" strokeLinecap="round" />
          <path d="M -4 -69 L -4.6 -60 M 4 -69 L 4.6 -60" stroke="rgba(255,255,255,0.7)" strokeWidth="1.1" strokeLinecap="round" />
          <path d="M -11 -49 Q 0 -46 11 -49 L 12 -41 L -12 -41 Z" fill="rgba(0,0,0,0.14)" />
        </g>
      )
  }
}

/** One leg with its shoe, pivoting at the hip so the stride is a rotation and nothing else. */
function Leg({ side }: { side: 'left' | 'right' }) {
  const x = side === 'left' ? -7.5 : 7.5
  const toe = side === 'left' ? -1 : 1

  return (
    <g className={`bh-leg bh-leg--${side}`}>
      <Limb d={`M ${x} -40 L ${x} -11`} color={PANTS} width={9} />
      <path
        d={`M ${x - 7 + toe * 2} -1 Q ${x - 7 + toe * 2} -10 ${x + toe * 2} -10 Q ${x + 7 + toe * 2} -10 ${x + 7 + toe * 2} -3 L ${x + 7 + toe * 2} -1 Z`}
        fill="#F1F3F7"
        stroke={INK}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d={`M ${x - 7 + toe * 2} -2.4 L ${x + 7 + toe * 2} -2.4`} stroke="#9AA3B2" strokeWidth="1.6" />
      <path d={`M ${x - 3 + toe * 2} -8.6 L ${x + 3.5 + toe * 2} -5`} stroke="#3E9BE6" strokeWidth="1.7" strokeLinecap="round" />
    </g>
  )
}

/* The racket's strings, computed to end on the frame rather than clipped by it: a clip path would
   need an id shared across every figure on the floor, and a mesh that knows its own shape needs
   nothing. */
const RACKET = { rx: 9.5, ry: 12.5 }
const RACKET_MESH = [
  ...[-6, -3, 0, 3, 6].map((x) => {
    const half = RACKET.ry * Math.sqrt(1 - (x * x) / (RACKET.rx * RACKET.rx))
    return `M ${x} ${-half} L ${x} ${half}`
  }),
  ...[-8, -4, 0, 4, 8].map((y) => {
    const half = RACKET.rx * Math.sqrt(1 - (y * y) / (RACKET.ry * RACKET.ry))
    return `M ${-half} ${y} L ${half} ${y}`
  }),
].join(' ')

/** An electric swatter, drawn as the small racket it is. Held, never swung, until a bug is fixed. */
function Racket() {
  return (
    <g transform="translate(41 -117) rotate(18)">
      <path d="M 0 12 L 0 28" stroke={INK} strokeWidth="5" strokeLinecap="round" />
      <path d="M 0 12 L 0 28" stroke="#E5484D" strokeWidth="2.6" strokeLinecap="round" />
      <path d={RACKET_MESH} stroke="#9AA6B8" strokeWidth="0.7" />
      <ellipse rx={RACKET.rx} ry={RACKET.ry} fill="none" stroke={INK} strokeWidth="4.2" />
      <ellipse rx={RACKET.rx} ry={RACKET.ry} fill="none" stroke="#E8ECF3" strokeWidth="2.4" />
    </g>
  )
}

export type Pose = 'hunting' | 'working' | 'resting'

/**
 * A developer.
 *
 * Three poses, each saying something true and nothing more:
 *
 * - **hunting** — an agent is working on a bug at this station. Standing, racket up, in a stride.
 * - **working** — an agent is reporting, on something that is not a bug. Standing with their
 *   notebook, or a tablet.
 * - **resting** — no agent is reporting. Seated at the desk, which is the neutral picture of a
 *   person at work. Deliberately not asleep and not "idle": §43 is explicit that the absence of
 *   telemetry is never drawn as the absence of work, and a sleeping figure would be exactly that.
 */
export const DeveloperFigure = memo(function DeveloperFigure({
  look,
  pose,
  swinging,
}: {
  look: Look
  pose: Pose
  swinging: boolean
}) {
  if (pose === 'resting') {
    return (
      // Lowered as a whole so the desk in front hides the waist down. The offset sits on an outer
      // group because the breathing animation owns the inner one's transform.
      <g transform="translate(0 24)">
        <g className="bh-figure">
          <Arm d="M -17 -69 Q -24 -57 -15 -51" look={look} />
          <Arm d="M 17 -69 Q 24 -57 15 -51" look={look} />
          <Torso look={look} />
          <circle cx="-15" cy="-51" r="4.2" fill={look.skin} stroke={INK} strokeWidth="1.3" />
          <circle cx="15" cy="-51" r="4.2" fill={look.skin} stroke={INK} strokeWidth="1.3" />
          <Head look={look} expression="rest" />
          <Companion kind={look.companion} />
        </g>
      </g>
    )
  }

  if (pose === 'working') {
    return (
      <g className="bh-figure">
        <ellipse cx="0" cy="0" rx="24" ry="5" fill="rgba(0,0,0,0.28)" />
        <Leg side="left" />
        <Leg side="right" />
        <Torso look={look} />

        {/* Their notebook — or tablet — held at the chest. */}
        <HeldLaptop model={look.laptop} />

        <Arm d="M -17 -69 Q -23 -56 -14 -50" look={look} />
        <Arm d="M 17 -69 Q 23 -56 14 -50" look={look} />
        <circle cx="-14" cy="-50" r="4.2" fill={look.skin} stroke={INK} strokeWidth="1.3" />
        <circle cx="14" cy="-50" r="4.2" fill={look.skin} stroke={INK} strokeWidth="1.3" />

        <Head look={look} expression="calm" />
        <Companion kind={look.companion} />
      </g>
    )
  }

  return (
    // Leaning into the stride, towards the bug — on an outer group, because the stride's bounce
    // animates the inner one's transform and would otherwise straighten the lean.
    <g transform="rotate(4)">
      <g className="bh-figure bh-figure--hunting">
        <ellipse cx="0" cy="0" rx="26" ry="5" fill="rgba(0,0,0,0.28)" />

        <g className="bh-arm-back">
          <Arm d="M -17 -69 Q -25 -60 -28 -50" look={look} />
          <circle cx="-28" cy="-50" r="4.2" fill={look.skin} stroke={INK} strokeWidth="1.3" />
        </g>

        <Leg side="left" />
        <Leg side="right" />
        <Torso look={look} />

        {/* Chest mark: the pulse from the logo, so the hoodie belongs to this product. */}
        <path d="M -7 -58 h 3.5 l 2 -4.5 l 3 8.5 l 2 -4 h 3.5" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />

        <Head look={look} expression="focused" />

        {/* The racket arm. Its rotation is animated in CSS, from the shoulder. */}
        <g className={`bh-arm${swinging ? ' bh-arm--swing' : ''}`}>
          {look.held === 'racket' ? <Racket /> : <HeldItem kind={look.held} />}
          <Arm d="M 17 -69 Q 29 -71 31 -89" look={look} />
          <circle cx="31.5" cy="-90" r="4.4" fill={look.skin} stroke={INK} strokeWidth="1.3" />
        </g>
        <Companion kind={look.companion} />
      </g>
    </g>
  )
})

/**
 * A face on its own, for the panels.
 *
 * The same `Head` as the floor, cropped, so the person in the card is recognisably the person in
 * the room.
 */
export const Portrait = memo(function Portrait({ look, size = 44 }: { look: Look; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="-32 -136 64 64" aria-hidden="true" className="bh-portrait">
      <rect x="-32" y="-136" width="64" height="64" fill={look.shirt} opacity="0.35" />
      <g transform="translate(0 4)">
        <Torso look={look} />
      </g>
      <Head look={look} expression="calm" />
    </svg>
  )
})

/* ------------------------------------------------------------------ furniture */

/**
 * A desk seen from the front, its feet at the origin.
 *
 * Order matters more than detail: a desk in front of a seated body reads as a workstation, a desk
 * behind a standing one reads as the place they got up from.
 */
export const Desk = memo(function Desk({ active, laptop }: { active: boolean; laptop?: LaptopModel }) {
  return (
    <g>
      {/* Monitor, off to one side so it never covers a face. */}
      <rect x="35" y="-41" width="4" height="12" fill="var(--bh-desk-edge)" stroke={INK} strokeWidth="1" />
      <rect x="20" y="-67" width="34" height="26" rx="3" fill="var(--bh-screen-frame)" stroke={INK} strokeWidth="1.5" />
      <rect x="22.5" y="-64.5" width="29" height="20" rx="1.5" className={active ? 'bh-screen bh-screen--on' : 'bh-screen'} />
      {active && (
        <g className="bh-code" stroke="var(--bh-code)" strokeWidth="1.3" strokeLinecap="round">
          <line x1="26" y1="-60" x2="38" y2="-60" />
          <line x1="26" y1="-55" x2="33" y2="-55" />
          <line x1="29" y1="-50" x2="45" y2="-50" />
        </g>
      )}

      <path d="M -52 -30 L 52 -30 L 56 -24 L -56 -24 Z" fill="var(--bh-desk-top)" stroke={INK} strokeWidth="1.4" strokeLinejoin="round" />
      <rect x="-54" y="-24" width="108" height="22" rx="2" fill="var(--bh-desk)" stroke={INK} strokeWidth="1.4" />
      <path d="M 18 -17 h 26 M 18 -9 h 26" stroke="rgba(0,0,0,0.3)" strokeWidth="1.2" />
      <rect x="-52" y="-2" width="6" height="3" fill={INK} />
      <rect x="46" y="-2" width="6" height="3" fill={INK} />

      {/* A notebook left open on the desk, or else the keyboard. */}
      {laptop && laptop !== 'tablet' ? (
        <DeskLaptop model={laptop} />
      ) : (
        <rect x="-17" y="-29.5" width="28" height="3.8" rx="1.2" fill="var(--bh-keyboard)" stroke={INK} strokeWidth="0.8" />
      )}
      <g transform="translate(-38 -33)">
        <rect x="-3" y="-4.5" width="6" height="7.5" rx="1.4" fill="var(--bh-mug)" stroke={INK} strokeWidth="1" />
        <path d="M 3 -2.5 a 2.4 2.4 0 0 1 0 4" fill="none" stroke={INK} strokeWidth="1.2" />
      </g>
    </g>
  )
})

/** The chair behind a seated figure, or pushed out beside the desk of one who is up. */
export const Chair = memo(function Chair() {
  return (
    <g>
      <rect x="-22" y="-80" width="44" height="50" rx="11" fill="var(--bh-chair)" stroke={INK} strokeWidth="1.5" />
      <rect x="-15" y="-73" width="30" height="36" rx="8" fill="rgba(255,255,255,0.06)" />
      <rect x="-2.5" y="-30" width="5" height="22" fill="var(--bh-desk-edge)" stroke={INK} strokeWidth="1" />
      <path d="M -16 -3 L 16 -3" stroke={INK} strokeWidth="3.4" strokeLinecap="round" />
    </g>
  )
})

/* ----------------------------------------------------------------- ai agent */

/**
 * The AI companion.
 *
 * Deliberately not a person. §5 of the brief asks that agents be legible as agents, and drawing
 * one as a second human would quietly imply headcount that does not exist — a manager glancing at
 * a floor of eight figures should not have to work out which four are software.
 */
export const AgentFigure = memo(function AgentFigure({ busy }: { busy: boolean }) {
  return (
    <g className={`bh-agent${busy ? ' bh-agent--busy' : ''}`}>
      {/* It hovers, so it gets a cast shadow and a thruster rather than legs. */}
      <ellipse cx="0" cy="24" rx="10" ry="2.8" fill="rgba(0,0,0,0.3)" />

      <g className="bh-agent__body">
        <ellipse className="bh-agent__thrust" cx="0" cy="13.5" rx="6" ry="3.2" fill="var(--bh-agent-eye)" opacity="0.55" />

        <rect x="-15" y="-13" width="30" height="26" rx="11" fill="var(--bh-agent)" stroke={INK} strokeWidth="1.6" />
        <rect x="-15" y="-13" width="30" height="10" rx="5" fill="rgba(255,255,255,0.35)" />
        <rect x="-10.5" y="-6" width="21" height="12" rx="6" fill="var(--bh-agent-visor)" stroke={INK} strokeWidth="1" />

        {/* Two lights that pulse while it works and hold steady when it does not. */}
        <ellipse cx="-4.5" cy="0" rx="2.2" ry="2.6" fill="var(--bh-agent-eye)" className="bh-agent__eye" />
        <ellipse cx="4.5" cy="0" rx="2.2" ry="2.6" fill="var(--bh-agent-eye)" className="bh-agent__eye" />

        <rect x="-18" y="-4" width="4" height="9" rx="2" fill="var(--bh-agent-edge)" stroke={INK} strokeWidth="1" />
        <rect x="14" y="-4" width="4" height="9" rx="2" fill="var(--bh-agent-edge)" stroke={INK} strokeWidth="1" />

        {/* Antenna, with a bead that only glows when there is real work behind it. */}
        <line x1="0" y1="-13" x2="0" y2="-20" stroke={INK} strokeWidth="1.6" />
        <circle cx="0" cy="-22" r="3" fill={busy ? 'var(--bh-agent-signal)' : 'var(--bh-agent-edge)'} stroke={INK} strokeWidth="1" className="bh-agent__signal" />
      </g>
    </g>
  )
})

/* ------------------------------------------------------------------ creature */

/**
 * A mosquito, facing left, centred on its thorax.
 *
 * Priority shows up three ways on purpose: the colour of the abdomen, a count of stripes on it,
 * and — wherever one is drawn — the id and status in words. Colour alone fails a colour-blind
 * reader and fails again in a screenshot pasted into a document.
 *
 * The wings rise up and back from the thorax and each beats from its own root. The first drawing
 * had them meeting over the body in a drooping arch that squashed around its middle, which looked
 * like wings hung upside down, and that is what people saw.
 */
export const BugCreature = memo(function BugCreature({
  priority,
  seed,
  weakened,
  still,
}: {
  priority: Priority
  seed: number
  /** Tilts as the work nears completion. Read from the server's number, never the other way. */
  weakened: boolean
  /** Contained or held: wings fold and the creature settles. */
  still: boolean
}) {
  const accent = PRIORITY_ACCENT[priority]
  const stripes = PRIORITY_MARKS[priority]

  // Weakening adds to the bug's own tilt rather than replacing it.
  const tilt = (seed % 7) - 3 + (weakened ? 14 : 0)

  return (
    <g className={`bh-bug${still ? ' bh-bug--still' : ''}`} transform={`rotate(${tilt})`}>
      <g className="bh-bug__wing bh-bug__wing--far">
        <path d="M 1 -5 C 4 -18 14 -30 24 -31 C 30 -31.5 30.5 -24 25.5 -18.5 C 18 -10 8 -6 1 -5 Z" fill="var(--bh-wing)" stroke="var(--bh-wing-edge)" strokeWidth="0.9" />
        <path d="M 2.5 -6.5 C 10 -14 17 -22 24 -28" fill="none" stroke="var(--bh-wing-edge)" strokeWidth="0.6" />
      </g>

      <g stroke="#D5DCE8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d="M -4 3 L -12 10 L -17 21" />
        <path d="M -1.5 4.5 L -6 14 L -8 25" />
        <path d="M 1.5 4.5 L 2.5 14 L 0.5 26" />
        <path d="M 3.5 3.5 L 12 11 L 17 22" />
        <path d="M 5 3 L 16 8 L 27 15" />
        <path d="M -3 4 L -10 13 L -13 24" />
      </g>

      <path d="M 4 -2.5 C 12 -7 26 -5.5 33 1.5 C 35.5 5 31.5 8.5 26.5 8.5 C 18 8.5 8 5.5 4 3.5 Z" fill={accent} stroke={INK} strokeWidth="1.2" strokeLinejoin="round" />
      {Array.from({ length: stripes }, (_, i) => {
        const x = 13 + i * 6
        return <path key={i} d={`M ${x} -4.6 Q ${x - 1.4} 1 ${x - 1} 6.8`} stroke="rgba(0,0,0,0.45)" strokeWidth="2" fill="none" />
      })}

      <ellipse cx="0" cy="0" rx="7.2" ry="6.6" fill="var(--bh-bug-body)" stroke={INK} strokeWidth="1.2" />
      <ellipse cx="-1.5" cy="-2.6" rx="3.4" ry="1.8" fill="#FFFFFF" opacity="0.18" />

      <path d="M -13.5 0.5 L -24 4.5" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M -11 -5.5 Q -14 -10.5 -17.5 -10.5 M -8.5 -6 Q -10.5 -11.5 -13 -12.5" fill="none" stroke={INK} strokeWidth="1" strokeLinecap="round" />

      <circle cx="-9" cy="-1" r="5.4" fill="var(--bh-bug-head)" stroke={INK} strokeWidth="1.2" />
      <circle cx="-10" cy="-2.4" r="3.5" fill="#FFFFFF" stroke={INK} strokeWidth="0.8" />
      <circle cx="-10.9" cy="-2.2" r="2" fill="#140C10" />
      <circle cx="-10.1" cy="-3.3" r="0.75" fill="#FFFFFF" />
      {/* One raised brow: mischief, not menace. This is a bug, not a threat model. */}
      <path d="M -13.8 -6.8 L -7.6 -5.4" stroke={INK} strokeWidth="1.2" strokeLinecap="round" />

      <g className="bh-bug__wing bh-bug__wing--near">
        <path d="M -1 -5 C -2 -20 6 -33 15 -35 C 21 -36 22 -29 18.5 -23 C 13 -14 6 -8 -1 -5 Z" fill="var(--bh-wing)" stroke="var(--bh-wing-edge)" strokeWidth="0.9" />
        <path d="M 0 -6.5 C 3 -16 8 -25 14 -31" fill="none" stroke="var(--bh-wing-edge)" strokeWidth="0.6" />
      </g>
    </g>
  )
})
